import { useBlocker, type ShouldBlockFn } from "@tanstack/react-router";

import { Button, Inline, Modal, Stack, Text } from "../ui";

interface AuthoringLeaveGuardProps {
  isDirty: boolean;
  isPending?: boolean;
  shouldBlockFn?: ShouldBlockFn;
  subject: string;
}

// UI-LAW §4c Authoring: one shared route-level leave guard for dirty forms.
// In-app navigations resolve through the Modal; refresh and tab-close use the
// browser's native beforeunload prompt.
export function AuthoringLeaveGuard({
  isDirty,
  isPending = false,
  shouldBlockFn = () => true,
  subject,
}: AuthoringLeaveGuardProps) {
  const blocker = useBlocker({
    disabled: !isDirty,
    enableBeforeUnload: () => isDirty,
    shouldBlockFn,
    withResolver: true,
  });
  const isBlocked = blocker.status === "blocked";
  const keepEditing = () => {
    blocker.reset?.();
  };

  return (
    <Modal
      ariaLabel={`Discard unsaved ${subject} changes?`}
      isOpen={isBlocked}
      onDismiss={keepEditing}
    >
      <Stack gap={4}>
        <Text>
          This {subject} has unsaved changes. Leaving this page discards them.
        </Text>
        <Inline gap={2}>
          <Button type="button" onClick={keepEditing}>
            Keep editing
          </Button>
          <Button
            disabled={isPending}
            type="button"
            variant="danger"
            onClick={() => {
              blocker.proceed?.();
            }}
          >
            Discard changes
          </Button>
        </Inline>
      </Stack>
    </Modal>
  );
}
